import { pickBy, uniq } from 'lodash';

import {
  AGE_BOUNDS,
  GGVAR_SCALE,
  MAX_AGE_RANGES,
  MIN_PRODUCT_REVIEWS,
  PROJECT_NAME_MAX_LENGTH,
  PROJECT_NAME_PATTERN,
  TUNING_RANGE_PERCENT,
} from './constants';
import {
  flavorsNeedingNewFlavors,
  flavorsNotInReport,
  newFlavorsMessage,
  notInReportMessage,
} from './flavorTokens';
import { getSteps, StepContext, StepId } from './steps';
import { FieldErrors, ReportRequestState } from './types';

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const PROJECT_NAME_RE = new RegExp(PROJECT_NAME_PATTERN);

/** Percent rounding tolerance when summing age range weights. */
const WEIGHT_EPSILON = 0.001;

export const isUuid = (value: string): boolean => UUID_RE.test(value);

export const validateProjectName = (name: string): string | undefined => {
  const trimmed = name.trim();
  if (!trimmed) return 'Project name is required';
  if (trimmed.length > PROJECT_NAME_MAX_LENGTH)
    return `Project name cannot exceed ${PROJECT_NAME_MAX_LENGTH} characters`;
  if (!/^[A-Za-z0-9]/.test(trimmed))
    return 'Project name must start with a letter or number';
  if (!PROJECT_NAME_RE.test(trimmed))
    return 'Only letters, numbers, spaces, hyphens and underscores are allowed';
  return undefined;
};

export const ageWeightTotal = (state: Pick<ReportRequestState, 'ageRanges'>) =>
  state.ageRanges.reduce((sum, r) => sum + (Number(r.fraction) || 0), 0);

const validateType = (state: ReportRequestState): FieldErrors =>
  state.reportKind ? {} : { reportKind: 'Choose the type of report' };

const validateProject = (state: ReportRequestState): FieldErrors => {
  const errors: FieldErrors = {};
  const nameError = validateProjectName(state.projectName);
  if (nameError) errors.projectName = nameError;
  if (!state.clientName.trim()) errors.clientName = 'Client is required';
  if (!state.workspace) errors.workspace = 'Workspace is required';
  return errors;
};

const validateDemographics = (state: ReportRequestState): FieldErrors => {
  const errors: FieldErrors = {};
  if (!state.country) errors.country = 'Choose the target country';

  if (state.includeFemale < 0 || state.includeFemale > 1)
    errors.includeFemale = 'Gender split must be between 0% and 100%';

  const bounds = AGE_BOUNDS;
  if (state.ageRanges.length === 0) {
    errors.ageRanges = 'Add at least one age range';
  } else if (state.ageRanges.length > MAX_AGE_RANGES) {
    errors.ageRanges = `At most ${MAX_AGE_RANGES} age ranges are supported`;
  }
  state.ageRanges.forEach((range, i) => {
    if (!Number.isInteger(range.min) || !Number.isInteger(range.max)) {
      errors[`ageRanges.${i}`] = 'Ages must be whole numbers';
    } else if (range.min >= range.max) {
      errors[`ageRanges.${i}`] = 'Minimum age must be below maximum age';
    } else if (range.min < bounds.min || range.max > bounds.max) {
      errors[`ageRanges.${i}`] =
        `Ages must be within ${bounds.min} - ${bounds.max}`;
    } else if (!(range.fraction > 0)) {
      errors[`ageRanges.${i}`] = 'Each range needs a weight above 0%';
    }
  });
  const total = ageWeightTotal(state);
  if (state.ageRanges.length > 0 && Math.abs(total - 1) > WEIGHT_EPSILON) {
    errors.ageRangesTotal = `Weights must add up to 100% (currently ${Math.round(
      total * 100,
    )}%)`;
  }

  if (!state.smokingHabits) errors.smokingHabits = 'Choose smoking habits';
  return errors;
};

/** Names with no product row in any of the allowed workspaces. */
export const unreachableProductNames = (
  state: Pick<ReportRequestState, 'products' | 'allowedWorkspaces'>,
): string[] => {
  const names = uniq(
    state.products.filter((p) => !p.original).map((p) => p.name),
  );
  return names.filter(
    (name) =>
      !state.products.some(
        (p) => p.name === name && state.allowedWorkspaces.includes(p.workspace),
      ),
  );
};

export const lowDataProductNames = (
  state: Pick<ReportRequestState, 'products'>,
): string[] => {
  const maxReviews = new Map<string, number>();
  state.products.forEach((p) =>
    maxReviews.set(p.name, Math.max(maxReviews.get(p.name) ?? 0, p.reviews)),
  );
  return Array.from(maxReviews.entries())
    .filter(([, reviews]) => reviews < MIN_PRODUCT_REVIEWS)
    .map(([name]) => name);
};

/** An update that adds nothing re-runs the report; allowed, but worth flagging. */
export const updateAddsNothing = (state: ReportRequestState): boolean => {
  if (state.mode !== 'update') return false;
  const original = state.updateSource?.originalWorkspaces ?? [];
  return (
    !state.products.some((p) => !p.original) &&
    state.allowedWorkspaces.every((w) => original.includes(w))
  );
};

export const uncategorisedProductNames = (
  state: Pick<ReportRequestState, 'products'>,
): string[] =>
  uniq(state.products.map((p) => p.name)).filter(
    (name) => !state.products.some((p) => p.name === name && !!p.category),
  );

const validateCompetitiveSet = (state: ReportRequestState): FieldErrors => {
  const errors: FieldErrors = {};
  const names = uniq(state.products.map((p) => p.name));
  if (names.length === 0) errors.products = 'Add at least one product';

  if (names.length > 0 && state.allowedWorkspaces.length === 0)
    errors.allowedWorkspaces = 'Allow at least one workspace';

  const unreachable = unreachableProductNames(state);
  if (unreachable.length > 0) {
    errors.allowedWorkspaces = `Not in any allowed workspace: ${unreachable.join(
      ', ',
    )}`;
  }

  if (names.length > 0 && state.categories.length === 0)
    errors.categories = 'Select at least one category';

  const hasName = state.customClassDbName.trim().length > 0;
  const hasProducts = state.customClassDbProducts.length > 0;
  if (hasName && !hasProducts)
    errors.customClassDb = 'Add products to the custom class database';
  if (!hasName && hasProducts)
    errors.customClassDb = 'Name the custom class database';

  return errors;
};

const validateParent = (state: ReportRequestState): FieldErrors => {
  if (!state.parent) return { parent: 'Select the parent report' };
  if (!state.inherited)
    return { parent: 'Waiting for the parent report parameters to load' };
  return {};
};

const validateOptimization = (
  state: ReportRequestState,
  context?: StepContext,
): FieldErrors => {
  const errors: FieldErrors = {};
  const opt = state.optimization;
  if (!opt.targetProduct)
    errors['optimization.targetProduct'] = 'Choose the product to optimize';
  if (opt.constraintLevels.length === 0)
    errors['optimization.constraintLevels'] =
      'Select at least one constraint level';
  if (opt.newReferenceFlavors.length === 0)
    errors['optimization.newReferenceFlavors'] =
      'Choose whether new reference flavors are allowed';
  if (!opt.optimizeFlavor && !opt.optimizeTexture)
    errors['optimization.target'] = 'Optimize flavor, texture, or both';

  const seenGgvars = new Set<string>();
  opt.ggvarConstraints.forEach((c, i) => {
    const key = `optimization.ggvarConstraints.${i}`;
    if (!c.key) errors[key] = 'Choose a GGVar';
    else if (seenGgvars.has(c.key))
      errors[key] = 'GGVar is already constrained';
    else if (!Number.isInteger(c.min) || !Number.isInteger(c.max))
      errors[key] = 'Values must be whole numbers';
    else if (c.min > c.max) errors[key] = 'Minimum must not exceed maximum';
    else if (c.min < GGVAR_SCALE.min || c.max > GGVAR_SCALE.max)
      errors[key] =
        `Values must be within ${GGVAR_SCALE.min} - ${GGVAR_SCALE.max}`;
    seenGgvars.add(c.key);
  });

  const lock = new Set(opt.refFlavorsToLock);
  const remove = new Set(opt.refFlavorsToRemove);
  const tune = opt.refFlavorsToTune.map((t) => t.flavor);
  const conflicts = uniq([
    ...opt.refFlavorsToRemove.filter((f) => lock.has(f)),
    ...tune.filter((f) => lock.has(f) || remove.has(f)),
  ]);
  if (conflicts.length > 0) {
    errors['optimization.refFlavors'] =
      `A reference flavor can only be locked, removed or tuned: ${conflicts.join(', ')}`;
  }

  opt.refFlavorsToTune.forEach((t, i) => {
    const key = `optimization.refFlavorsToTune.${i}`;
    if (!Number.isFinite(t.min) || !Number.isFinite(t.max))
      errors[key] = 'Enter a minimum and maximum';
    else if (t.min > t.max) errors[key] = 'Minimum must not exceed maximum';
    else if (
      t.min < TUNING_RANGE_PERCENT.min ||
      t.max > TUNING_RANGE_PERCENT.max
    )
      errors[key] =
        `Range must be within ${TUNING_RANGE_PERCENT.min}% - ${TUNING_RANGE_PERCENT.max}%`;
  });

  const parentFlavors = context?.parentFlavors ?? [];
  const notInReport = flavorsNotInReport(opt, parentFlavors);
  if (notInReport.length > 0)
    errors['optimization.refFlavorsNotInReport'] =
      notInReportMessage(notInReport);

  if (!opt.newReferenceFlavors.includes('TRUE')) {
    const shares = context?.targetFlavorShares ?? new Map();
    const pinned = flavorsNeedingNewFlavors(opt, shares, parentFlavors);
    if (pinned.length > 0 && !errors['optimization.refFlavors']) {
      errors['optimization.refFlavors'] = newFlavorsMessage(pinned, shares);
    }
  }
  return errors;
};

/**
 * Shown as soon as they apply: range rows, so an inverted or empty range is
 * flagged at once, and flavors the optimizer can't use, whose notice is always shown.
 */
const LIVE_ERROR_KEY =
  /^optimization\.((ggvarConstraints|refFlavorsToTune)\.\d+|refFlavorsNotInReport)$/;

/** The errors to show before the user has tried to submit. */
export const liveErrors = (errors: FieldErrors): FieldErrors =>
  pickBy(errors, (_, key) => LIVE_ERROR_KEY.test(key));

/** A section's errors to show: all of them once the user has tried to submit. */
export const visibleErrors = (
  errors: FieldErrors | undefined,
  attempted: boolean,
): FieldErrors => (attempted ? errors ?? {} : liveErrors(errors ?? {}));

const validateMixAndMatch = (state: ReportRequestState): FieldErrors => {
  const errors: FieldErrors = {};
  if (!state.optimization.targetProduct)
    errors['optimization.targetProduct'] = 'Choose the product to optimize';
  if (state.mixAndMatch.length === 0)
    errors.mixAndMatch = 'Select at least one optimization report';
  state.mixAndMatch.forEach((m) => {
    if (m.versions.length === 0)
      errors[`mixAndMatch.${m.reportId}`] = 'Select at least one version';
  });
  return errors;
};

const validateOptions = (state: ReportRequestState): FieldErrors => {
  const errors: FieldErrors = {};
  const inherit = state.reportIdToInherit.trim();
  if (inherit && !isUuid(inherit))
    errors.reportIdToInherit = 'Must be a report ID (UUID)';
  return errors;
};

const validateSource = (state: ReportRequestState): FieldErrors => {
  const errors: FieldErrors = {};
  if (!state.updateSource)
    errors.updateSource =
      'Open this page from a report\u2019s "Update" action to choose what to update';
  // Identity is inherited and read-only; legacy names predate the naming rules.
  if (!state.projectName.trim())
    errors.projectName = 'Project name is required';
  if (!state.clientName.trim()) errors.clientName = 'Client is required';
  if (!state.workspace) errors.workspace = 'Workspace is required';
  return errors;
};

const VALIDATORS: Record<
  StepId,
  (state: ReportRequestState, context?: StepContext) => FieldErrors
> = {
  type: validateType,
  project: validateProject,
  source: validateSource,
  demographics: validateDemographics,
  competitiveSet: validateCompetitiveSet,
  parent: validateParent,
  optimization: validateOptimization,
  mixAndMatch: validateMixAndMatch,
  options: validateOptions,
};

export const validateStep = (
  stepId: StepId,
  state: ReportRequestState,
  context?: StepContext,
): FieldErrors => VALIDATORS[stepId](state, context);

/** Errors across every step of the current flow, keyed by step. */
export const validateAllSteps = (
  state: ReportRequestState,
  context: StepContext,
): Partial<Record<StepId, FieldErrors>> =>
  getSteps(state, context).reduce(
    (acc, s) => {
      const errors = validateStep(s.id, state, context);
      if (Object.keys(errors).length > 0) acc[s.id] = errors;
      return acc;
    },
    {} as Partial<Record<StepId, FieldErrors>>,
  );
